<!--
  VoucherRegistrationsTab — 바우처관리 › 등록 내역
  · GET /voucher/org/{org}/vouchers?classroom_idx&barcode&q&date_from&date_to&status&page&size (페이지 봉투)
  · code(바우처 코드)는 CS 용으로 화면에 표시해도 된다
-->
<template>
  <a-card title="등록 내역" :sub="`총 ${data.total}건 · 학생이 앱에서 지정 도서를 등록한 기록`" overflow="visible">
    <div class="p-filters">
      <select v-model="filters.classroom_idx" class="p-input p-select">
        <option value="">전체 클래스</option>
        <option v-for="c in classrooms" :key="c.classroom_idx" :value="String(c.classroom_idx)">{{ classroomOptionLabel(c) }}</option>
      </select>
      <select v-model="filters.status" class="p-input p-select">
        <option value="">전체 상태</option>
        <option value="REGISTERED">등록 완료</option>
        <option value="PENDING">처리 중</option>
        <option value="FAILED">실패</option>
      </select>
      <input v-model.trim="filters.q" type="text" class="p-input" placeholder="학생·도서·코드 검색" @keyup.enter="search" />
      <span class="p-filters__label">기간</span>
      <input v-model="filters.date_from" type="date" class="p-input" />
      <span class="p-filters__label">~</span>
      <input v-model="filters.date_to" type="date" class="p-input" />
      <a-btn kind="primary" size="sm" :disabled="loading" @click="search">조회</a-btn>
      <a-btn kind="ghost" size="sm" @click="reset">초기화</a-btn>
    </div>

    <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
    <a-empty v-else-if="!data.items.length" title="등록 내역이 없습니다" />
    <template v-else>
      <a-table :columns="cols" :rows="data.items" row-key="voucher_idx">
        <template #cell-registered_at="{ row }"><span class="p-mono">{{ fmtDateTime(row.registered_at || row.created_at) }}</span></template>
        <template #cell-student="{ row }">
          <div class="p-cell-title">{{ row.student_name || '-' }}</div>
          <div class="p-cell-sub p-mono">{{ row.student_email || '' }}</div>
        </template>
        <template #cell-classroom_name="{ value }"><span>{{ value || '-' }}</span></template>
        <template #cell-title="{ row }">
          <div class="p-cell-title">{{ row.title }}</div>
          <div class="p-cell-sub p-mono">{{ row.barcode }}</div>
        </template>
        <template #cell-grade="{ value }"><a-pill kind="yellow">{{ gradeLabel(value) }}</a-pill></template>
        <template #cell-amount="{ row }">
          <div class="p-amount">{{ fmtAmount(row.amount, row.currency_code) }}</div>
          <div v-if="row.list_price != null" class="p-cell-sub">정가 {{ fmtAmount(row.list_price) }} · 할인 {{ row.discount_rate }}%</div>
        </template>
        <template #cell-status="{ row }">
          <a-pill :kind="voucherStatusKind(row.status)">{{ voucherStatusLabel(row.status) }}</a-pill>
          <div v-if="row.fail_reason" class="p-cell-sub">{{ row.fail_reason }}</div>
        </template>
        <template #cell-code="{ value }"><span class="p-mono">{{ value || '-' }}</span></template>
      </a-table>
      <div v-if="totalPages > 1" class="p-pagination">
        <pagination :current-page="data.page" :total-pages="totalPages" @update:current-page="load" />
      </div>
    </template>
  </a-card>
</template>

<script>
import ACard from '@/components/Pineapple/ACard.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import Pagination from '@/components/Pagination.vue';
import { fmtAmount, fmtDateTime, gradeLabel, classStatusLabel, voucherStatusKind, voucherStatusLabel, voucherErrorText } from '../orgUtils';

const SIZE = 50;
const emptyFilters = () => ({ classroom_idx: '', status: '', q: '', date_from: '', date_to: '' });

export default {
  name: 'VoucherRegistrationsTab',
  components: { ACard, ATable, APill, ABtn, AEmpty, Pagination },
  props: {
    orgIdx: { type: [Number, String], required: true },
  },
  data() {
    return {
      classrooms: [],
      filters: emptyFilters(),
      data: { items: [], total: 0, page: 1, size: SIZE },
      loading: false,
      cols: [
        { key: 'registered_at',  label: '등록일',  width: '200px' },
        { key: 'student',        label: '학생' },
        { key: 'classroom_name', label: '클래스',  width: '160px' },
        { key: 'title',          label: '도서' },
        { key: 'grade',          label: '등급',    width: '90px' },
        { key: 'amount',         label: '차감액',  width: '140px', align: 'right' },
        { key: 'status',         label: '상태',    width: '110px' },
        { key: 'code',           label: '코드',    width: '190px' },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.data.total || 0) / (this.data.size || SIZE)));
    },
  },
  mounted() {
    this.loadClassrooms();
    this.load(1);
  },
  methods: {
    fmtAmount, fmtDateTime, gradeLabel, voucherStatusKind, voucherStatusLabel,
    classroomOptionLabel(c) {
      return c.class_status && c.class_status !== 'ongoing' ? `${c.name} (${classStatusLabel(c.class_status)})` : c.name;
    },
    async loadClassrooms() {
      try {
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/classrooms`, params: { status: 'all' }, loadingBar: false }); // 종료·취소 클래스의 등록 건도 조회 대상 — all (2026-09-22 백엔드 권고)
        this.classrooms = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.classrooms = [];
      }
    },
    async load(page) {
      this.loading = true;
      try {
        const params = { page, size: SIZE };
        Object.entries(this.filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/vouchers`, params, loadingBar: false });
        const d = res.data || {};
        this.data = {
          items: Array.isArray(d.items) ? d.items : [],
          total: Number(d.total) || 0,
          page: Number(d.page) || page,
          size: Number(d.size) || SIZE,
        };
      } catch (e) {
        this.data = { items: [], total: 0, page: 1, size: SIZE };
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '등록 내역을 불러오지 못했습니다.') });
      } finally {
        this.loading = false;
      }
    },
    search() { this.load(1); },
    reset() {
      this.filters = emptyFilters();
      this.load(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';
</style>
